import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit, ChangeDetectorRef, signal, computed, WritableSignal, Signal, effect, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Subject, of, Observable, Subscription } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, tap, finalize, take, startWith, map } from 'rxjs/operators';
import { Factura, FiltrosFactura, ApiResponse } from '@app/types';
import { InvoiceMonitorService } from './invoice-monitor.component.service';
import { CustomSnackBarService } from '@app/shared/components/custom-snack-bar/custom-snack-bar.service';
import { CustomMessageService } from '@app/shared/components/custom-message/custom-message.service';
import { Router } from '@angular/router';
import { InvoiceDataService } from '../../services/entities/invoice.service';
import { StatusTranslatorService } from '@app/services/utils/status-translator.service';
import { SimpleExcelService } from '@app/services/utils/simple-excel.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { DatePipe } from '@angular/common';

import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatTabsModule } from '@angular/material/tabs';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';
import { CustomTitleComponent } from '@app/shared/components/custom-title/custom-title.component';
import { MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ObservationsDialogComponent } from './observations-dialog/observations-dialog.component';
import { UploadStatusDialogComponent } from './upload-status-dialog/upload-status-dialog.component';
import { MatDivider } from '@angular/material/divider';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';


@Component({
    selector: 'app-invoice-monitor',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        MatExpansionModule,
        MatFormFieldModule,
        MatInputModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatButtonModule,
        MatCheckboxModule,
        MatSelectModule,
        MatIconModule,
        MatAutocompleteModule,
        MatPaginatorModule,
        MatProgressSpinnerModule,
        MatDivider,
        ClipboardModule,
        MatTooltipModule,
        MatCardModule,
        MatTabsModule,
        MatMenuModule, // Añadido para dropdown de metadata
    ],
    templateUrl: './invoice-monitor.component.html',
    styleUrl: './invoice-monitor.component.scss',
    providers: [{ provide: MAT_DATE_LOCALE, useValue: 'es-MX' }],
})
export class InvoiceMonitorComponent implements OnInit, OnDestroy, AfterViewInit {
    /**
     * @description FormGroup for managing filter inputs.
     */
    filtroForm: FormGroup;

    /**
     * @description Signal holding the paginated list of invoices to display.
     */
    facturasPaginadasSignal: Signal<Factura[]>;
    /**
     * @description Signal holding the total number of invoices after filtering.
     */
    totalFacturasFiltradasSignal: Signal<number>;

    /**
     * @description Signal indicating if all filtered invoices are currently selected.
     */
    todasSeleccionadasSignal: Signal<boolean>;

    // Loading State
    apiLoadingSignal: Signal<boolean>;
    isFilteringSignal: WritableSignal<boolean> = signal(false);
    /**
     * @description Signal indicating if data is currently being loaded or processed.
     */
    isLoadingSignal: Signal<boolean>;

    /**
     * @description Signal for search functionality
     */
    searchSignal: WritableSignal<string> = signal('');

    // --- PROPIEDADES PARA AUTOCOMPLETADO ---
    filteredClientes$!: Observable<string[]>;
    filteredEmisores$!: Observable<string[]>; // <-- NUEVA LÍNEA
    filteredRfcs$!: Observable<string[]>;
    // --- FIN ---

    /**
     * @description Array of available statuses for invoices.
     */
    estatusDisponibles = computed(() => {
        const statuses = this.statusTranslator.getStatuses()();
        return statuses
            .filter(s => !s.deleted)
            .sort((a, b) => a.order - b.order)
            .map(s => s.status);
    });
    /**
     * @description Model for the selected status when applying a batch status change.
     */
    estatusSeleccionadoParaCambio: string = '';

    @ViewChild(MatPaginator) paginator!: MatPaginator;

    /**
     * @description WritableSignal to manage the loading state of the status update action.
     */
    isUpdatingStatusSignal: WritableSignal<boolean> = signal(false);

    /**
     * @description WritableSignal holding the current page event from MatPaginator.
     */
    public pageEventSignal: WritableSignal<PageEvent> = signal<PageEvent>({ pageIndex: 0, pageSize: 20, length: 0 });

    private destroy$ = new Subject<void>();

    // Propiedades para Avedi Health
    avediMetadataSignal: WritableSignal<any> = signal(null);
    private http = inject(HttpClient);
    private simpleExcel = inject(SimpleExcelService);

    activeTabSignal: WritableSignal<number> = signal(0); // 0: Facturas, 1: Pre-facturas, 2: Remisiones

    private currentRequestSubscription?: Subscription; // Para cancelar peticiones al cambiar de tab

    // Para detectar cambios individuales en las fechas (como en Dashboard)
    private rangoFechaInicioChange$ = new Subject<void>();
    private rangoFechaFinChange$ = new Subject<void>();

    currentTitle = computed(() => {
        switch (this.activeTabSignal()) {
            case 0: return 'Monitor de Facturas';
            case 1: return 'Monitor de Pre-facturas';
            case 2: return 'Monitor de Remisiones';
            default: return 'Monitor de Facturas';
        }
    });

    constructor(
        private fb: FormBuilder,
        public invoiceMonitorService: InvoiceMonitorService,
        private snackBar: CustomSnackBarService,
        private customMessage: CustomMessageService,
        private cdr: ChangeDetectorRef,
        private dialog: MatDialog,
        private router: Router,
        private invoiceDataService: InvoiceDataService, // <-- RE-AÑADIR ESTA INYECCIÓN
        private statusTranslator: StatusTranslatorService
    ) {
        // ... existing constructor code ...
        this.filtroForm = this.fb.group({
            cliente: [''],
            emisor: [''], // <-- NUEVA LÍNEA
            rfc: [''],
            textoGeneral: [''],
            rangoFechas: this.fb.group({
                inicio: [null as Date | null],
                fin: [null as Date | null],
            }),
            estatus: [''],
        });

        this.apiLoadingSignal = this.invoiceMonitorService.isLoadingSignal;
        this.isLoadingSignal = computed(() => this.apiLoadingSignal() || this.isFilteringSignal()); // Combinar API y filtrado local
        this.todasSeleccionadasSignal = this.invoiceMonitorService.todasFiltradasEstanSeleccionadasSignal;
        this.totalFacturasFiltradasSignal = computed(() => this.invoiceMonitorService.facturasFiltradasYSeleccionadasSignal().length);

        // --- LÓGICA PARA AUTOCOMPLETADO ---
        this.filteredClientes$ = this.filtroForm.get('cliente')!.valueChanges.pipe(
            startWith(''),
            map(value => this._filterAutocomplete((value || '') as string, (this.invoiceMonitorService as any).clientesUnicosSignal()))
        );

        this.filteredEmisores$ = this.filtroForm.get('emisor')!.valueChanges.pipe( // <-- NUEVO BLOQUE
            startWith(''),
            map(value => this._filterAutocomplete((value || '') as string, (this.invoiceMonitorService as any).emisoresUnicosSignal()))
        );

        this.filteredRfcs$ = this.filtroForm.get('rfc')!.valueChanges.pipe(
            startWith(''),
            map(value => this._filterAutocomplete((value || '') as string, (this.invoiceMonitorService as any).rfcsUnicosSignal()))
        );
        // --- FIN LÓGICA AUTOCOMPLETADO ---

        this.facturasPaginadasSignal = computed(() => {
            const facturasFiltradas = this.invoiceMonitorService.facturasFiltradasYSeleccionadasSignal();
            const searchTerm = this.searchSignal().toLowerCase().trim();

            // Apply search filter if there's a search term
            let filteredInvoices = facturasFiltradas;
            if (searchTerm) {
                filteredInvoices = facturasFiltradas.filter(factura => {
                    const searchableText = [
                        factura.serie,
                        factura.folio,
                        factura.uuid,
                        factura.emisorNombre,
                        factura.emisorRFC,
                        factura.receptorNombre,
                        factura.receptorRFC,
                        factura.total?.toString(),
                        factura.fechaEmision?.toString(),
                        factura.fechaTimbrado?.toString(),
                        factura.ordenSurtimiento?.folioOrden,
                        factura.ordenSurtimientoId
                    ].join(' ').toLowerCase();

                    return searchableText.includes(searchTerm);
                });
            }

            const pageEvent = this.pageEventSignal();
            const startIndex = pageEvent.pageIndex * pageEvent.pageSize;
            const endIndex = startIndex + pageEvent.pageSize;
            return filteredInvoices.slice(startIndex, endIndex);
        });

        // Effect to handle paginator length and page index changes
        effect(() => {
            const totalFiltradas = this.totalFacturasFiltradasSignal();
            const currentPageEvent = this.pageEventSignal();
            if (this.paginator) {
                if (this.paginator.length !== totalFiltradas) {
                    this.paginator.length = totalFiltradas;
                }
                // Si la página actual ya no es válida después de filtrar, ir a la primera página
                if (totalFiltradas > 0 && currentPageEvent.pageIndex * currentPageEvent.pageSize >= totalFiltradas && this.paginator.pageIndex !== 0) {
                    this.paginator.firstPage(); // Esto disparará un nuevo evento de página
                } else if (totalFiltradas === 0 && this.paginator.pageIndex !== 0) {
                    this.paginator.firstPage();
                }
            }
            this.cdr.detectChanges(); // Puede ser necesario para actualizar la vista del paginador
        });

        // Configurar debounce para cambios individuales en fechas (como en Dashboard)
        this.rangoFechaInicioChange$.pipe(
            debounceTime(400),
            takeUntil(this.destroy$)
        ).subscribe(() => this.checkAndApplyDateFilter());

        this.rangoFechaFinChange$.pipe(
            debounceTime(400),
            takeUntil(this.destroy$)
        ).subscribe(() => this.checkAndApplyDateFilter());
    }

    ngOnInit(): void {

        // Carga inicial
        this.currentRequestSubscription = this.loadDataForCurrentTab().pipe(
            takeUntil(this.destroy$)
        ).subscribe({
            error: (err) => console.error('Error en carga inicial:', err)
        });

        this.invoiceMonitorService.actualizarFiltros({}); // Carga inicial
        this.loadAvediMetadata(); // Cargar información de Avedi

        // Manejar cambios en el formulario general con debounce
        this.filtroForm.valueChanges
            .pipe(
                tap(() => this.isFilteringSignal.set(true)), // Feedback inmediato
                debounceTime(400),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
                takeUntil(this.destroy$),
            )
            .subscribe((valores) => {
                // Desacoplar proceso para renderizar spinner con tiempo adecuado
                setTimeout(() => {
                    const rango = valores.rangoFechas;
                    const filtrosPayload: FiltrosFactura = {
                        cliente: valores.cliente,
                        emisor: valores.emisor,
                        rfc: valores.rfc,
                        estatus: valores.estatus,
                        textoGeneral: valores.textoGeneral,
                        rangoFechas: rango && rango.inicio && rango.fin ? { inicio: rango.inicio, fin: rango.fin } : undefined,
                    };
                    this.invoiceMonitorService.actualizarFiltros(filtrosPayload);
                    this.isFilteringSignal.set(false); // Apagar spinner
                }, 100); // Tiempo mínimo para que el spinner sea visible pero no moleste
            }, (error) => {
                console.error('Error en suscripción de filtros:', error);
                this.isFilteringSignal.set(false); // Asegurar que el spinner se desactive en caso de error
            });

        // Exponer función global para debug
        (window as any).forceRefreshNow = () => this.forzarRefrescoCompleto(true);
    }

    onTabChange(index: number): void {
        this.activeTabSignal.set(index);

        // Asegurar que el spinner de filtrado esté apagado al cambiar de tab
        this.isFilteringSignal.set(false);

        this.limpiarFiltros();

        // Cancelar petición anterior si existe
        if (this.currentRequestSubscription) {
            this.currentRequestSubscription.unsubscribe();
        }

        this.currentRequestSubscription = this.loadDataForCurrentTab().pipe(
            takeUntil(this.destroy$)
        ).subscribe({
            error: (err) => {
                console.error('Error al cargar datos del tab:', err);
                this.snackBar.open('Error al cargar datos', 'error');
            }
        });
    }

    private loadDataForCurrentTab(): Observable<Factura[]> {
        const tabIndex = this.activeTabSignal();
        switch (tabIndex) {
            case 0: return this.invoiceDataService.loadInvoices(true);
            case 1: return this.invoiceDataService.loadPreInvoices();
            case 2: return this.invoiceDataService.loadReferrals();
            default: return of([]);
        }
    }

    loadAvediMetadata(): void {
        this.http.get<any>(`${environment.API_URL}/avedi/admin/health`)
            .pipe(
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: (data) => {
                    this.avediMetadataSignal.set(data);
                },
                error: (err) => {
                    console.error('[InvoiceMonitor] ❌ Error cargando metadata Avedi:', err);
                    // Establecer un valor por defecto en caso de error para que la UI no se rompa
                    this.avediMetadataSignal.set({
                        connection_status: 'Error de conexión',
                        files_metadata: [],
                        last_sync_attempt: null,
                        last_system_sync: 'No disponible',
                        status: 'error'
                    });
                }
            });
    }

    ngAfterViewInit(): void {
        if (this.paginator) {
            this.paginator.page
                .pipe(
                    tap((event) => this.pageEventSignal.set(event)),
                    takeUntil(this.destroy$),
                )
                .subscribe();

            // Sincronización inicial del paginador con los datos existentes
            const initialLength = this.invoiceMonitorService.facturasFiltradasYSeleccionadasSignal().length;
            if (this.paginator.length !== initialLength) {
                this.paginator.length = initialLength;
            }
            // Establecer el estado inicial del pageEventSignal basado en el paginador
            this.pageEventSignal.set({
                pageIndex: this.paginator.pageIndex,
                pageSize: this.paginator.pageSize,
                length: this.paginator.length,
            });
            this.cdr.detectChanges();
        }
    }

    /**
     * @description Clears all filter fields and triggers an update by resetting the form.
     */
    limpiarFiltros(): void {
        // Asegurarse de que el spinner no se quede activado
        this.isFilteringSignal.set(false);

        // Safety net: asegurar que se apague después de cualquier posible ciclo de eventos
        // por si el reset dispara valueChanges inadvertidamente
        setTimeout(() => {
            this.isFilteringSignal.set(false);
        }, 300);

        this.filtroForm.reset(
            {
                cliente: '',
                emisor: '',
                rfc: '',
                textoGeneral: '',
                rangoFechas: { inicio: null, fin: null },
                estatus: '',
            },
            { emitEvent: false }, // ⭐ Evita que valueChanges se dispare
        );

        // Forzar actualización de los filtros en el servicio
        this.invoiceMonitorService.actualizarFiltros({});

        // Resetear paginación
        this.pageEventSignal.set({ pageIndex: 0, pageSize: 20, length: 0 });
    }

    /**
     * @description Forcibly reloads all invoice data from the server, resetting all filters and selections.
     */
    public forzarRefrescoCompleto(fromAction: boolean = false): void {
        const successMessage = fromAction ? 'Operación exitosa. Datos actualizados.' : 'Datos actualizados correctamente.';
        this.snackBar.open('Actualizando datos desde el servidor...', 'info', 1.5);
        this.limpiarFiltros();

        // Cancelar petición anterior si existe
        if (this.currentRequestSubscription) {
            this.currentRequestSubscription.unsubscribe();
        }

        this.currentRequestSubscription = this.loadDataForCurrentTab().pipe(
            take(1),
            takeUntil(this.destroy$)
        ).subscribe({
            next: (payload) => {
                this.snackBar.open(successMessage, 'success', 3);

                if (this.paginator) {
                    this.paginator.firstPage();
                    // Mantener sincronizado el signal con el estado del paginador después del reset
                    this.pageEventSignal.set({
                        pageIndex: this.paginator.pageIndex,
                        pageSize: this.paginator.pageSize,
                        length: this.paginator.length,
                    });
                }
                this.cdr.detectChanges();
            },
            error: (err) => {
                console.error('[InvoiceMonitor] ❌ Error en refresh:', err);
                this.snackBar.open('Error al actualizar datos', 'error', 3);
            }
        });
    }

    /**
     * @description Handles the change event of an individual invoice checkbox.
     * @param facturaId The ID of the invoice.
     * @param event The MatCheckboxChange event.
     */
    onFacturaSelectionChange(facturaId: string, event: any): void {
        this.invoiceMonitorService.seleccionarFactura(facturaId, event.checked);
    }

    /**
     * @description Handles the change event of the "select all filtered" checkbox.
     * This selects/deselects all invoices matching the current filters, across all pages.
     * @param event The MatCheckboxChange event.
     */
    onSelectAllFiltradasChange(event: any): void {
        this.invoiceMonitorService.seleccionarTodasLasFiltradas(event.checked);
    }

    /**
     * @description Applies the selected status change to all selected invoices.
     * Con logging detallado para trazar el flujo.
     */
    aplicarCambioDeEstatus(): void {
        const idsSeleccionadas = this.invoiceMonitorService.getFacturasSeleccionadasIds();
        const nuevoEstatus = this.estatusSeleccionadoParaCambio;


        if (!nuevoEstatus) {
            this.snackBar.open('Por favor, seleccione un nuevo estatus.', 'warning');
            return;
        }
        if (idsSeleccionadas.length === 0) {
            this.snackBar.open('No hay facturas seleccionadas para cambiar el estatus.', 'info');
            return;
        }

        this.customMessage
            .open({
                title: 'Confirmar Cambio de Estatus',
                message: `¿Está seguro que desea cambiar el estatus de ${idsSeleccionadas.length} factura(s) a "${nuevoEstatus}"?`,
                type: 'info',
                acceptBtn: true,
                dismissBtn: true,
            })
            .then((confirmed) => {
                if (confirmed) {
                    const dialogRef = this.dialog.open(ObservationsDialogComponent, {
                        width: '400px',
                        data: {
                            facturas: idsSeleccionadas,
                            status: nuevoEstatus
                        },
                    });

                    dialogRef.afterClosed().subscribe(result => {
                        if (result && result.observation) {
                            this.isUpdatingStatusSignal.set(true);
                            this.invoiceMonitorService
                                .cambiarEstatusSeleccionadas(nuevoEstatus, result.observation)
                                .pipe(
                                    takeUntil(this.destroy$),
                                    finalize(() => this.isUpdatingStatusSignal.set(false))
                                )
                                .subscribe({
                                    next: (response) => {
                                        if (response.state === 'success') {
                                            this.snackBar.open('Estatus de facturas actualizado correctamente.', 'success');
                                            this.estatusSeleccionadoParaCambio = '';
                                            if (this.paginator) {
                                                this.paginator.firstPage();
                                            }
                                        } else {
                                            this.snackBar.open(response.message || 'Ocurrió un error al actualizar.', 'error');
                                        }
                                    },
                                    error: (err) => {
                                        console.error('PASO 9 - ERROR: [Componente] - Error en la suscripción.', err);
                                        this.snackBar.open('Ocurrió un error inesperado en la comunicación.', 'error');
                                    }
                                });
                        }
                    });
                }
            });
    }

    /**
     * @description Generates a CSS class string based on the invoice status for styling.
     * @param estatus The status string of the invoice.
     * @returns A string representing the CSS class.
     */
    getEstatusClass(estatus: string): string {
        if (!estatus) {
            return 'status-desconocido';
        }
        return 'status-' + estatus.toLowerCase().replace(/\s+/g, '-');
    }

    /**
     * @description Color del catálogo para el chip de estatus (undefined => usa el CSS por defecto).
     */
    getStatusColor(estatus: string): string | undefined {
        return this.statusTranslator.getColor(estatus);
    }

    /**
     * @description Returns the 'rangoFechas' FormGroup from the main form.
     * This is a helper for the template to correctly bind [formGroup].
     * @returns The FormGroup for 'rangoFechas'.
     */
    get rangoFechasFormGroup(): FormGroup {
        return this.filtroForm.get('rangoFechas') as FormGroup;
    }

    // Método de apoyo: filtrado local para opciones de autocompletado (con límite de 50)

    // --- NUEVO MÉTODO PRIVADO PARA FILTRAR AUTOCOMPLETADO ---
    private _filterAutocomplete(value: string, options: string[]): string[] {
        const filterValue = (value || '').toLowerCase();
        const filteredOptions = options.filter(option => (option || '').toLowerCase().includes(filterValue));
        // Limitar a 50 resultados para evitar problemas de rendimiento
        if (filteredOptions.length > 50) {
            return [...filteredOptions.slice(0, 50), `... y ${filteredOptions.length - 50} más`];
        }
        return filteredOptions;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    goBack(): void {
        this.router.navigate(['/home']);
    }

    onCopyToClipboard(text: string): void {
        this.snackBar.open(`'${text}' copiado al portapapeles.`, 'info');
    }

    /**
     * @description Determines the color for the metadata sync icon based on file status
     * @param meta The metadata object from Avedi
     * @returns The Material color name for the icon
     */
    getMetadataSyncStatus(meta: any): string {
        if (!meta || !meta.files_metadata || meta.files_metadata.length === 0) {
            return 'accent'; // Sin archivos (Info)
        }

        // [UI-05] Si no hay fecha de sync del sistema, es una alerta (WARN)
        if (!meta.last_system_sync || meta.last_system_sync === 'No disponible') {
            return 'warn';
        }

        const hasOutdated = meta.files_metadata.some((file: any) =>
            file.last_modified_source > meta.last_system_sync
        );

        if (hasOutdated) {
            return 'warn'; // Archivos desactualizados
        }

        return 'primary'; // Todo sincronizado
    }

    /**
     * @description Devuelve una etiqueta legible para el estado del último sync
     * @param status Estado del sync (SUCCESS | ERROR | RUNNING | NEVER_RUN)
     * @returns Etiqueta corta para el badge
     */
    getSyncStatusLabel(status: string | undefined | null): string {
        switch (status) {
            case 'SUCCESS': return 'OK';
            case 'ERROR': return 'Error';
            case 'RUNNING': return 'En curso';
            default: return '—';
        }
    }

    /**
     * @description Returns the most recent last_modified_source date from all files
     * @param files Array of file metadata objects
     * @returns The most recent date string or null if no files
     */
    getLatestSourceDate(files: any[]): string | null {
        if (!files || files.length === 0) {
            return null;
        }

        const dates = files
            .map(f => f.last_modified_source)
            .filter(d => d != null);

        if (dates.length === 0) {
            return null;
        }

        // Encontrar la fecha más reciente
        return dates.reduce((latest, current) => {
            return current > latest ? current : latest;
        });
    }

    /**
     * @description Translates a payment method code (PPD/PUE) to a human-readable string.
     * @param code The payment method code.
     * @returns The descriptive string.
     */
    getMetodoPagoDescripcion(code: string | null | undefined): string {
        if (!code) return 'No especificado';
        const map: { [key: string]: string } = {
            'PPD': 'Pago en parcialidades o diferido',
            'PUE': 'Pago en una sola exhibición',
        };
        return map[code] || 'No especificado';
    }

    /**
     * @description Translates a tax regime code to a human-readable string.
     * @param code The tax regime code.
     * @returns The descriptive string.
     */
    getRegimenFiscalDescripcion(code: string | null | undefined): string {
        if (!code) return 'Desconocido';
        const map: { [key: string]: string } = {
            '601': 'General de Ley Personas Morales',
            '603': 'Personas Morales con Fines no Lucrativos',
            '626': 'Régimen Simplificado de Confianza',
            '605': 'Sueldos y Salarios e Ingresos Asimilados a Salarios',
            '606': 'Arrendamiento',
            '607': 'Régimen de Enajenación o Adquisición de Bienes',
            '608': 'Demás ingresos',
            '610': 'Consolidación',
            '611': 'Sociedades Cooperativas de Producción que optan por diferir sus ingresos',
            '612': 'Incorporación Fiscal',
            '614': 'Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras',
            '615': 'Régimen de los Ingresos por Dividendos (Socios y Accionistas)'
        };
        return map[code] || 'Desconocido';
    }

    /**
     * @description Translates a CFDI usage code to a human-readable string.
     * @param code The CFDI usage code.
     * @returns The descriptive string.
     */
    getUsoCFDIDescripcion(code: string | null | undefined): string {
        if (!code) return 'Desconocido';
        const map: { [key: string]: string } = {
            'G01': 'Adquisición de mercancías',
            'G03': 'Gastos en general',
            'I08': 'Otra maquinaria y equipo',
            'S01': 'Sin efectos fiscales',
            'P01': 'Por definir',
            'D01': 'Donativos',
            'T01': 'Traslados de mercancías',
            'X01': 'Sin especificar',
            // Agregar más códigos según sea necesario
        };
        return map[code] || 'Desconocido';
    }

    getStatusName(statusKey: string): string {
        return this.statusTranslator.translate(statusKey, 'cobranza');
    }

    /**
     * Exporta a Excel las facturas actualmente filtradas (respetando filtros activos).
     */
    async exportarExcel(): Promise<void> {
        const facturas = this.invoiceMonitorService.facturasFiltradasYSeleccionadasSignal();
        if (!facturas || facturas.length === 0) {
            this.snackBar.open('No hay facturas para exportar.', 'warning');
            return;
        }

        const data = facturas.map(f => ({
            folio: f.folio,
            serie: f.serie,
            uuid: f.uuid,
            fechaEmision: f.fechaEmision,
            fechaVencimiento: f.fechaVencimiento,
            emisorNombre: f.emisorNombre,
            emisorRFC: f.emisorRFC,
            receptorNombre: f.receptorNombre,
            receptorRFC: f.receptorRFC,
            ordenSurtimiento: f.ordenSurtimiento?.folioOrden || f.folioOrden || '',
            estatus: this.getStatusName(f.estatus),
            subtotal: f.subtotal,
            iva: f.iva,
            total: f.total,
            moneda: f.moneda,
        }));

        const today = new Date().toISOString().slice(0, 10);
        await this.simpleExcel.simpleXLSX({
            fileName: `Monitor-Avedi-${today}`,
            sheets: [{
                sheetName: 'Facturas',
                header: ['Monitor AVEDI - Facturas filtradas', `Total: ${data.length}`, 'Epione'],
                lineBreak: 1,
                columns: [
                    { columnKey: 'folio', columnName: 'Folio', width: 12 },
                    { columnKey: 'serie', columnName: 'Serie', width: 10 },
                    { columnKey: 'uuid', columnName: 'Folio Fiscal (UUID)', width: 40 },
                    { columnKey: 'fechaEmision', columnName: 'Fecha de Emisión', type: 'date', width: 18 },
                    { columnKey: 'fechaVencimiento', columnName: 'Fecha de Vencimiento', type: 'date', width: 18 },
                    { columnKey: 'emisorNombre', columnName: 'Emisor', width: 35 },
                    { columnKey: 'emisorRFC', columnName: 'RFC Emisor', width: 15 },
                    { columnKey: 'receptorNombre', columnName: 'Receptor', width: 35 },
                    { columnKey: 'receptorRFC', columnName: 'RFC Receptor', width: 15 },
                    { columnKey: 'ordenSurtimiento', columnName: 'Orden de Surtimiento', width: 25 },
                    { columnKey: 'estatus', columnName: 'Estatus', width: 22 },
                    { columnKey: 'subtotal', columnName: 'Subtotal', type: 'currency', width: 15 },
                    { columnKey: 'iva', columnName: 'IVA', type: 'currency', width: 12 },
                    { columnKey: 'total', columnName: 'Total', type: 'currency', width: 15 },
                    { columnKey: 'moneda', columnName: 'Moneda', width: 10 },
                ],
                data,
            }],
        });
        this.snackBar.open(`Excel generado con ${data.length} factura(s).`, 'success');
    }

    openUploadStatusDialog(): void {
        const dialogRef = this.dialog.open(UploadStatusDialogComponent, {
            width: '900px',
            disableClose: true,
            data: {
                allInvoices: this.invoiceDataService.facturasSignal(), // Pasar las facturas de Avedi
                isAvedi: true // Indicar que sí es el monitor de Avedi
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result === true) {
                this.snackBar.open('La carga masiva ha finalizado, actualizando vista...', 'info');
            }
        });
    }

    // Métodos específicos para cambios en fechas (como en Dashboard)
    onRangoFechaInicioChange() {
        this.rangoFechaInicioChange$.next();
    }

    onRangoFechaFinChange() {
        this.rangoFechaFinChange$.next();
    }

    // Método para verificar si hay rango completo y aplicar filtro
    checkAndApplyDateFilter() {
        const rango = this.filtroForm.get('rangoFechas')?.value;
        // Solo aplicar filtro si ambas fechas están establecidas
        if (rango && rango.inicio && rango.fin) {
            // Verificar que ambas fechas son válidas (no null/undefined)
            const inicioValida = rango.inicio instanceof Date && !isNaN(rango.inicio.getTime());
            const finValida = rango.fin instanceof Date && !isNaN(rango.fin.getTime());

            if (inicioValida && finValida) {
                this.onFilterChange();
            }
        }
    }

    // Método para manejar cambios en filtros (como en Dashboard)
    onFilterChange() {
        // Desacoplar proceso para renderizar spinner con tiempo adecuado
        setTimeout(() => {
            const rango = this.filtroForm.get('rangoFechas')?.value;
            const filtrosPayload: FiltrosFactura = {
                cliente: this.filtroForm.get('cliente')?.value,
                emisor: this.filtroForm.get('emisor')?.value,
                rfc: this.filtroForm.get('rfc')?.value,
                estatus: this.filtroForm.get('estatus')?.value,
                textoGeneral: this.filtroForm.get('textoGeneral')?.value,
                rangoFechas: rango && rango.inicio && rango.fin ? { inicio: rango.inicio, fin: rango.fin } : undefined,
            };
            this.invoiceMonitorService.actualizarFiltros(filtrosPayload);
            this.isFilteringSignal.set(false); // Apagar spinner
        }, 100); // Tiempo mínimo para que el spinner sea visible pero no moleste
    }
}
